import React, { useState } from 'react';
import { Card, Rate, Tag, Button, Badge, Tooltip, Space, Typography, Image, Modal } from 'antd';
import { HeartOutlined, HeartFilled, ShoppingCartOutlined, EyeOutlined, StarOutlined } from '@ant-design/icons';
import { getImageForCard, getImageForDetail, getImageWithFallback, handleImageError, getAllImages } from '../../utils/images';
import ModernImageGallery from './ModernImageGallery';
import ImageCarousel from './ImageCarousel';

const { Text, Title } = Typography;

const MarketplaceMenuCard = ({ 
  menu, 
  onAddToCart, 
  onViewDetails, 
  onToggleFavorite,
  isFavorite = false,
  showAddToCart = true,
  showFavorite = true,
  showQuickView = true,
  cardStyle = {},
  imageHeight = 200,
  showVendor = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [galleryVisible, setGalleryVisible] = useState(false);

  const handleImageClick = () => {
    if (showQuickView) {
      setGalleryVisible(true);
    } else if (onViewDetails) {
      onViewDetails(menu);
    }
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(menu);
    }
  };

  const handleToggleFavorite = (e) => {
    e.stopPropagation();
    if (onToggleFavorite) {
      onToggleFavorite(menu);
    }
  };

  const handleViewDetails = (e) => {
    e.stopPropagation();
    if (onViewDetails) {
      onViewDetails(menu);
    }
  };

  // Price display removed - prices only shown in quotations

  // Hide preparation time per requirements
  const getPreparationTime = () => null;

  const getDietaryTags = () => {
    const tags = [];
    if (menu.is_vegetarian) tags.push({ text: 'Veg', color: 'green' });
    if (menu.is_vegan) tags.push({ text: 'Vegan', color: 'lime' });
    if (menu.is_gluten_free) tags.push({ text: 'Gluten Free', color: 'blue' });
    if (menu.is_spicy) tags.push({ text: 'Spicy', color: 'red' });
    return tags;
  };

  const dietaryTags = getDietaryTags();
  const allImages = getAllImages(menu, 'medium');
  const hasMultipleImages = allImages.length > 1;

  return (
    <>
      <Card
        hoverable
        className="marketplace-menu-card"
        style={{
          borderRadius: '16px',
          overflow: 'hidden',
          border: '1px solid #f0f0f0',
          transition: 'all 0.3s ease',
          transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
          boxShadow: isHovered ? '0 12px 28px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.08)',
          background: '#ffffff',
          ...cardStyle
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        bodyStyle={{ padding: 0 }}
      >
        {/* Image Section - Modern Food App Style */}
        <div 
          className="relative cursor-pointer flex items-center justify-center"
          style={{ height: `${imageHeight}px`, overflow: 'hidden', backgroundColor: '#f8f9fa' }}
          onClick={handleImageClick}
        >
          {hasMultipleImages ? (
                   <ImageCarousel
                     item={menu}
                     size="medium"
                     height={imageHeight}
                     showIndicators={true}
                     showArrows={true}
                     showPreview={true}
                     autoplay={true}
                     maxImages={3}
                   />
          ) : (
            <Image
              src={getImageWithFallback(menu, 'medium')}
              alt={menu.name}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                objectPosition: 'center',
                transition: 'transform 0.3s ease',
                transform: isHovered ? 'scale(1.05)' : 'scale(1)'
              }}
              preview={false}
              fallback="/images/placeholder.svg"
              onError={handleImageError}
            />
          )}
          
          {/* Gradient Overlay */}
          <div 
            className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"
            style={{ opacity: isHovered ? 1 : 0.7 }}
          />

          {/* Top Right Actions */}
          <div className="absolute top-3 right-3 flex flex-col gap-2">
            {/* Favorite Button */}
            {showFavorite && (
              <button
                onClick={handleToggleFavorite}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  isFavorite 
                    ? 'bg-red-500 text-white shadow-lg' 
                    : 'bg-white/90 text-gray-600 hover:bg-white'
                }`}
              >
                {isFavorite ? <HeartFilled className="text-sm" /> : <HeartOutlined className="text-sm" />}
              </button>
            )}
          </div>

          {/* Bottom Left - Dietary Tags */}
          {dietaryTags.length > 0 && (
            <div className="absolute bottom-3 left-3 flex gap-1">
              {dietaryTags.slice(0, 2).map((tag, index) => (
                <span 
                  key={index} 
                  className="px-2 py-1 bg-white/90 backdrop-blur-sm text-xs font-medium rounded-full"
                  style={{ color: tag.color === 'green' ? '#16a34a' : tag.color === 'red' ? '#dc2626' : '#6b7280' }}
                >
                  {tag.text}
                </span>
              ))}
              {dietaryTags.length > 2 && (
                <span className="px-2 py-1 bg-white/90 backdrop-blur-sm text-xs font-medium rounded-full text-gray-600">
                  +{dietaryTags.length - 2}
                </span>
              )}
            </div>
          )}

          {/* Bottom Right - Time hidden */}

          {/* Hover Actions Overlay */}
          <div 
            className="absolute inset-0 bg-black/20 transition-all duration-300 flex items-center justify-center"
            style={{ opacity: isHovered ? 1 : 0 }}
          >
            <div className="flex gap-3">
              {showQuickView && (
                <button
                  onClick={handleViewDetails}
                  className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                >
                  <EyeOutlined className="text-blue-500" />
                </button>
              )}
              {showAddToCart && (
                <button
                  onClick={handleAddToCart}
                  className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                >
                  <ShoppingCartOutlined className="text-white" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content Section - Modern Food App Style */}
        <div className="p-4">
          {/* Title and Rating Row */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-gray-900 truncate mb-1">
                {menu.name}
              </h3>
              {menu.rating && (
                <div className="flex items-center gap-1">
                  <div className="flex items-center">
                    <StarOutlined className="text-yellow-400 text-xs" />
                    <span className="text-sm font-medium text-gray-700 ml-1">
                      {menu.rating.toFixed(1)}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    ({menu.total_reviews || 0})
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {menu.description && (
            <p className="text-sm text-gray-600 line-clamp-2 mb-3 leading-relaxed">
              {menu.description}
            </p>
          )}

          {/* Vendor Info */}
          {showVendor && menu.vendor && (
            <div className="mb-3">
              <span className="text-xs text-gray-500">
                by {menu.vendor.business_name}
              </span>
            </div>
          )}

          {/* Bottom Row - Add Button only */}
          <div className="flex items-center justify-between">
            <div className="flex-1" />
            
            {showAddToCart && (
              <button
                onClick={handleAddToCart}
                className="px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-1"
              >
                <ShoppingCartOutlined className="text-xs" />
                Add
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* Image Gallery Modal */}
      <Modal
        open={galleryVisible}
        onCancel={() => setGalleryVisible(false)}
        footer={null}
        width="80%"
        style={{ top: 20 }}
        bodyStyle={{ padding: 0 }}
      >
        <ModernImageGallery
          images={[{
            original: getImageForDetail(menu),
            thumbnail: getImageForCard(menu),
            description: menu.description
          }]}
        />
      </Modal>
    </>
  );
};

export default MarketplaceMenuCard;
